// novem atom — section label (NovemHR-ekranlar port).
//
// The uppercase 11px micro-heading from the novem design (mockup
// SectionLabel / ERSecLabel). Used to head a group of fields, a sidebar
// nav cluster, or a card region. The skin's `.nai-section-label` class
// already exists (styles.css); this component is the missing wrapper so
// consumers stop re-typing the class string at every call site.
//
// Polymorphic `as`: defaults to a non-semantic <div> (a section label is
// decorative grouping, not a document heading — keeping it out of the
// heading outline avoids polluting screen-reader heading navigation).
// Callers that genuinely want it in the heading tree pass as="h2" etc.
//
// Accessibility: the host owns the i18n'd text (children). No ARIA is
// added — when as="div" it's plain text; when promoted to a heading the
// native element carries the role.

import { forwardRef, type ElementType, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface SectionLabelProps extends HTMLAttributes<HTMLElement> {
  /** The label text (host supplies the i18n key result). */
  readonly children: ReactNode;
  /** Element to render. Defaults to 'div' (decorative, not a heading). */
  readonly as?: ElementType;
}

export const SectionLabel = forwardRef<HTMLElement, SectionLabelProps>(
  ({ as, className, children, ...rest }, ref) => {
    const Component = (as ?? "div") as ElementType;
    return (
      <Component
        ref={ref}
        data-component="section-label"
        className={cx("nai-section-label", className)}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);
SectionLabel.displayName = "SectionLabel";
